import { createColumnHelper } from "@tanstack/react-table";
import classNames from "classnames";
import { ReactNode } from "react";

import { DataTable } from "@/components/DataTable";
import { FormatDate } from "@/components/Date";
import { SOSEntityHashLink } from "@/components/SosEntityHashLink";
import { SosEntityLink } from "@/components/SosEntityLink";
import { SosCustomFields } from "@/utils/sos/custom-fields";
import { SalesOrderLineItem } from "../queries/tracking-data/sales-orders";
import { LineItemDescription } from "./LineItemDescription";
import { JobTrackingInternalLink } from "./JobTrackingInternalLink";
import { withJobTrackingData } from "./withJobTrackingData";
import { LineItemUOMQuantity } from "@/features/job-tracking/components/LineItemUOMQuantity";

interface TableLineItem extends SalesOrderLineItem {
  wrongJob: boolean;
}

const columnHelper = createColumnHelper<TableLineItem>();

const columns = [
  columnHelper.accessor("lineNumber", {
    header: "#",
    cell: ({ row, getValue }) => (
      <>
        {row.original.wrongJob ? "⚠️" : ""}
        {getValue()}
      </>
    ),
  }),
  columnHelper.accessor("item.name", {
    header: "Item",
    cell: ({ row }) => (
      <JobTrackingInternalLink trackerPath="bom-by-component">
        {({ targetPath, targetPageTitle }) => (
          <SOSEntityHashLink
            fromHash={row.id}
            urlPath={targetPath}
            tooltipContent={`View on '${targetPageTitle}' page`}
            type="item"
            id={row.original.item.id}
            label={row.original.item.name}
          />
        )}
      </JobTrackingInternalLink>
    ),
  }),
  columnHelper.accessor("description", {
    header: "Description",
    cell: ({ row }) => <LineItemDescription line={row.original} />,
  }),
  columnHelper.accessor("uomQuantity", {
    header: "Quantity",
    cell: ({ row }) => <LineItemUOMQuantity line={row.original} />,
  }),
  columnHelper.accessor("job.name", {
    header: "Job",
    cell: ({ row, getValue }) => (
      <>
        {row.original.wrongJob ? "⚠️" : ""}
        {getValue()}
      </>
    ),
  }),
];

export const SalesOrderDetails = withJobTrackingData(({ job, salesOrder }) => {
  const evolvedOpportunityNumbers = [
    SosCustomFields.getNumberValue(job, "evolvedOpportunityNumber"),
    SosCustomFields.getNumberValue(job, "serviceEstimateOpportunityNumber"),
  ]
    .filter((x): x is number => !!x)
    .map((x) => `GNM#${x.toFixed()}`)
    .join(", ");

  const orderDetails: { label: string; value: ReactNode | undefined | null }[] = [
    { label: "Job #", value: <SosEntityLink type="job" id={job.id} label={job.number} /> },
    { label: "Sales Order #", value: <SosEntityLink type="salesorder" id={salesOrder.id} label={salesOrder.number} /> },
    { label: "Evolved Opportunity #s", value: evolvedOpportunityNumbers },
    { label: "Date Opened", value: <FormatDate date={SosCustomFields.getDateValue(job, "dateOpened")} /> },
    {
      label: "Date Closed",
      value: <FormatDate date={SosCustomFields.getDateValue(job, "dateClosed")} nullishValue="N/A" />,
    },
    {
      label: "Customer",
      value: <SosEntityLink type="customer" id={salesOrder.customer.id} label={salesOrder.customer.name} />,
    },
    { label: "Model", value: SosCustomFields.getStringValue(job, "model") },
    { label: "Pressure System", value: SosCustomFields.getBoolValue(job, "pressureSystem") ? "✅" : "❌" },
    { label: "Electrical Designer", value: SosCustomFields.getStringValue(job, "electricalDesigner") || "N/A" },
    { label: "Mechanical Designer", value: SosCustomFields.getStringValue(job, "mechanicalDesigner") || "N/A" },
  ];

  const lines = salesOrder.allLines.map<TableLineItem>((line) => ({
    ...line,
    wrongJob: line.job?.id !== job.id,
  }));

  return (
    <>
      <dl className="row">
        {orderDetails.map(({ label, value }, index, array) => (
          <div key={label} className="col-12 col-md-6 col-lg-4 col-xl-3">
            <dt>{label}</dt>
            <dd className={classNames({ "m-0": index === array.length - 1 })}>{value}</dd>
          </div>
        ))}
      </dl>
      <h3>Line Items</h3>
      <DataTable
        bordered
        hover
        columns={columns}
        data={lines}
        initialState={{ sorting: [{ id: "lineNumber", desc: false }] }}
      />
    </>
  );
});
